import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import Header from "@/features/roster-management/components/molecules/Header";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import SkeletonList from "@/components/organisms/SkeletonList";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { RosterManagementLeagueFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the LeaguesView component
 */
interface Props {
  leagues: RosterManagementLeagueFragment[];
  loading?: boolean;
  refreshing: boolean;
  bottomInset?: number;
  widthContent: number;
  renderItem: ListRenderItem<RosterManagementLeagueFragment>;
  onLoadMore: () => void;
  onRefresh: () => void;
  onSearch: (value: string) => void;
  renderListFooterComponent: () => ReactElement | null;
}

/**
 * Empty state shown when no leagues are available for the organization.
 * Prompts users to associate leagues with their organization.
 */
const ListEmptyComponent = () => (
  <Box className="h-4/5 items-center justify-center">
    <Text variant="unstyled" className={ifWeb("text-title-sm text-center", "")}>
      There are no eligible leagues for your organization. Please associate
      leagues with your organization to view leagues.
    </Text>
  </Box>
);
const keyExtractor = (item: RosterManagementLeagueFragment) => item.id;

/**
 * View for displaying a searchable, scrollable list of leagues.
 * @param root0 - Component props
 * @param root0.leagues - Array of league items to display
 * @param root0.loading - Whether initial data is loading
 * @param root0.refreshing - Whether pull-to-refresh is active
 * @param root0.bottomInset - Bottom safe area padding
 * @param root0.widthContent - Fixed width for content container
 * @param root0.renderItem - Function to render each league list item
 * @param root0.onLoadMore - Callback when scrolling near end to load more leagues
 * @param root0.onRefresh - Callback to refresh league data
 * @param root0.onSearch - Callback when search query changes with search value
 * @param root0.renderListFooterComponent - Function to render custom list footer
 */
const LeaguesView = ({
  leagues,
  loading,
  refreshing,
  bottomInset,
  widthContent,
  renderItem,
  onLoadMore,
  onRefresh,
  onSearch,
  renderListFooterComponent,
}: Props) => (
  <Box className="flex-1 pb-4">
    <FlashList
      contentContainerClassName="self-center flex-grow p-3"
      scrollEventThrottle={16}
      refreshing={refreshing}
      contentContainerStyle={{
        width: widthContent,
        paddingBottom: bottomInset,
      }}
      showsVerticalScrollIndicator={false}
      data={leagues}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ListEmptyComponent={
        loading ? <SkeletonList type="manage-roster" /> : ListEmptyComponent
      }
      ListHeaderComponent={
        <Header onChangeText={onSearch} placeholder="Search League" />
      }
      ListFooterComponent={renderListFooterComponent}
      onEndReached={onLoadMore}
      onRefresh={onRefresh}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={["white"]}
          tintColor="white"
        />
      }
      ItemSeparatorComponent={ItemSeparator}
    />
  </Box>
);

LeaguesView.displayName = "LeaguesView";

export default memo(LeaguesView);
